import { AlertDialog } from '@base-ui/react/alert-dialog'
import { Dialog } from '@base-ui/react/dialog'
import { type ReactNode, type RefObject, useId, useState } from 'react'
import { overlayCopy } from '@/copy'
import { Button } from '../Button/Button'
import { cx } from '../cx'
import { TextField } from '../Field/Field'
import { Icon } from '../Icon/Icon'
import { IconButton } from '../IconButton/IconButton'
import { Inline } from '../layout/Inline'
import { useOverlayLayer } from '../overlay/escapeStack'
import { OverlayHeader, overlayClass } from '../overlay/Overlay'
import styles from './Modal.module.css'

// Wraps Base UI Dialog (Modal) and AlertDialog (ConfirmDialog, D65).
// Deviations: Escape via the page-wide stack; closeDisabled cancels every
// close reason. Tested.

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  closeDisabled = false,
  footer,
  initialFocus,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  size?: 'sm' | 'md' | 'lg'
  closeDisabled?: boolean
  footer?: ReactNode
  initialFocus?: RefObject<HTMLElement | null>
  children?: ReactNode
}) {
  const handle = useOverlayLayer(open, (next, details) => {
    if (next) return
    if (closeDisabled) {
      details.cancel()
      return
    }
    onClose()
  })

  return (
    <Dialog.Root
      open={open}
      onOpenChange={handle}
      disablePointerDismissal={closeDisabled}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={overlayClass.scrim} data-layer="modal" />
        <Dialog.Viewport className={styles.viewport}>
          <Dialog.Popup
            initialFocus={initialFocus}
            className={cx(overlayClass.surface, styles.popup)}
            aria-modal="true"
            data-weight="lg"
            data-size={size}
          >
            <OverlayHeader
              title={
                <Dialog.Title
                  // biome-ignore lint/a11y/useHeadingContent: Base UI Title supplies the children (the title text)
                  render={<h2 />}
                  className={overlayClass.title}
                >
                  {title}
                </Dialog.Title>
              }
              close={
                <Dialog.Close
                  disabled={closeDisabled}
                  render={
                    <IconButton label={overlayCopy.close}>
                      <Icon name="cross-2" />
                    </IconButton>
                  }
                />
              }
            />
            <div className={styles.body}>
              {children}
              {footer && (
                <Inline gap="space-8" justify="end">
                  {footer}
                </Inline>
              )}
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  tone,
  onConfirm,
  loading = false,
  confirmText,
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: ReactNode
  confirmLabel: string
  tone: 'danger' | 'default'
  onConfirm: () => void
  loading?: boolean
  confirmText?: string
}) {
  const fieldId = useId()
  const cancelId = useId()
  const [typed, setTyped] = useState('')
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (!open) setTyped('')
  }

  const handle = useOverlayLayer(open, (next, details) => {
    if (next) return
    if (loading) {
      details.cancel()
      return
    }
    onClose()
  })

  return (
    <AlertDialog.Root open={open} onOpenChange={handle}>
      <AlertDialog.Portal>
        <AlertDialog.Backdrop
          className={overlayClass.scrim}
          data-layer="modal"
        />
        <AlertDialog.Viewport className={styles.viewport}>
          <AlertDialog.Popup
            // The safest control: the field when one must be typed, else Cancel.
            // `Button` takes no ref, so the element is found by id. A disabled
            // control (Cancel while loading) cannot take focus, so fall back to
            // the first tabbable rather than leave focus behind the dialog.
            initialFocus={() => {
              const target = document.getElementById(
                confirmText === undefined ? cancelId : fieldId,
              )
              return target?.matches(':disabled') ? true : target
            }}
            className={cx(overlayClass.surface, styles.popup)}
            aria-modal="true"
            data-weight="lg"
            data-size="sm"
          >
            <OverlayHeader
              title={
                <AlertDialog.Title
                  // biome-ignore lint/a11y/useHeadingContent: Base UI Title supplies the children (the title text)
                  render={<h2 />}
                  className={overlayClass.title}
                >
                  {title}
                </AlertDialog.Title>
              }
              close={
                <AlertDialog.Close
                  disabled={loading}
                  render={
                    <IconButton label={overlayCopy.close}>
                      <Icon name="cross-2" />
                    </IconButton>
                  }
                />
              }
            />
            <div className={styles.body}>
              {description && (
                <AlertDialog.Description
                  render={<p />}
                  className={styles.description}
                >
                  {description}
                </AlertDialog.Description>
              )}
              {confirmText !== undefined && (
                <TextField
                  id={fieldId}
                  label={overlayCopy.typeToConfirm(confirmText)}
                  value={typed}
                  onValue={setTyped}
                />
              )}
              <Inline gap="space-8" justify="end">
                <Button
                  variant="outline"
                  ground="surface"
                  id={cancelId}
                  disabled={loading}
                  onClick={onClose}
                >
                  {overlayCopy.cancel}
                </Button>
                <Button
                  variant={tone === 'danger' ? 'danger' : 'primary'}
                  ground="surface"
                  loading={loading}
                  disabled={confirmText !== undefined && typed !== confirmText}
                  onClick={onConfirm}
                >
                  {confirmLabel}
                </Button>
              </Inline>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Viewport>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
